import { useState } from 'react';
import { Box, Button, TextField, Typography, useTheme, alpha } from '@mui/material';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import dayjs from 'dayjs';
import type { DiscountCode } from '../../utils/subscriptionApi';

interface DiscountRowProps {
  discount: DiscountCode | null;
  onApply: (code: string) => Promise<void>;
  onRemove: () => Promise<void>;
}

export const DiscountRow: React.FC<DiscountRowProps> = ({ discount, onApply, onRemove }) => {
  const theme = useTheme();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    if (!code.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await onApply(code.trim().toUpperCase());
      setCode('');
    } catch (err) {
      setError((err as Error).message || 'Could not apply that code.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2.25,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        flexWrap: 'wrap',
      }}
    >
      <LocalOfferOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      {discount ? (
        <>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
              Discount code{' '}
              <Box
                component="span"
                sx={{
                  fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
                  bgcolor: 'background.paper',
                  px: 1,
                  py: 0.25,
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, 0.16),
                  borderRadius: 0.5,
                }}
              >
                {discount.code}
              </Box>{' '}
              applied
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {discount.percentOff}% off · Expires {dayjs(discount.expiresAt).format('MMM D, YYYY')}
            </Typography>
          </Box>
          <Button size="small" onClick={onRemove}>Remove</Button>
        </>
      ) : (
        <>
          <TextField
            placeholder="Enter discount code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            size="small"
            sx={{
              flex: 1,
              minWidth: 220,
              '& input': { textTransform: 'uppercase', letterSpacing: '0.04em' },
            }}
            error={!!error}
            helperText={error ?? undefined}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleApply();
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleApply}
            disabled={!code.trim() || submitting}
          >
            Apply
          </Button>
        </>
      )}
    </Box>
  );
};
